import React, { useState, useEffect } from 'react';
import { BringzzButton, BringzzText } from '@bringzz/components';
import useRequest from 'hooks/useRequest';
import { useOrder } from 'context/OrderContext';

export const ShopperInvoiceView = ({ invoice, onDownload, showDownload }) => {
  if (!invoice) return null;
  const currentYear = new Date().getFullYear();
  const formattedInvoiceId = invoice.invoiceNumber ? `${currentYear}-${invoice.invoiceNumber}` : '-';
  return (
    <div className="mt-4 p-4 border rounded bg-sand w-full max-w-md">
      <BringzzText tag="h5">Invoice number: #{formattedInvoiceId}</BringzzText>
      <BringzzText tag="h5">Created: {invoice.created || '-'}</BringzzText>
      <BringzzText tag="h5">Total: €{invoice.totalPrice ?? '-'}</BringzzText>
      <BringzzText tag="h5">Products:</BringzzText>
      <ul className="text-left">
        {invoice.products && invoice.products.length > 0 ? (
          invoice.products.map((prod) => (
            <li key={prod.id}>
              {prod.name} x{prod.quantity} - €{prod.price}
            </li>
          ))
        ) : (
          <li>No products</li>
        )}
      </ul>
      {showDownload && (
        <BringzzButton className="bg-magic-lilac mt-4" onClick={onDownload}>Download PDF</BringzzButton>
      )}
    </div>
  );
};

const ShopperInvoiceStep = ({ orderId, onContinue, showDownload = false }) => {
  const { sendRequest, loading } = useRequest();
  const { shopperInvoiceData, fetchShopperInvoiceData } = useOrder();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (shopperInvoiceData) {
        setInvoice(shopperInvoiceData);
        return;
      }
      
      setError(null);
      try {
        const data = await fetchShopperInvoiceData(orderId);
        setInvoice(data);
      } catch (err) {
        setError('Failed to fetch invoice.');
      }
    };
    if (orderId) fetchInvoice();
  }, [orderId, shopperInvoiceData, fetchShopperInvoiceData]);

  const handleDownload = async () => {
    if (!invoice) return;
    setError(null);
    try {
      const response = await sendRequest({
        method: 'POST',
        url: '/module/order/downloadShopperInvoicePdf',
        data: { orderId },
        responseType: 'json',
      });
      if (response.status === 'success' && response.data && response.data.content) {
        const { content, filename } = response.data;
        const base64 = content.split(',')[1];
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `shopper_invoice_${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        setError('Failed to download PDF.');
      }
    } catch (err) {
      setError('Failed to download PDF.');
    }
  };

  return (
    <div className="flex flex-col items-center text-center border border-black rounded-lg pb-4 my-6">
      <BringzzText tag="h4" className="font-normal mb-4">Get your invoice for this order</BringzzText>
      {loading && <BringzzText tag="h5">Loading...</BringzzText>}
      {invoice && <ShopperInvoiceView invoice={invoice} onDownload={handleDownload} showDownload={showDownload} />}
      {error && <BringzzText tag="h5" className="text-red-600 mt-2">{error}</BringzzText>}
      {!showDownload && onContinue && (
        <BringzzButton className="bg-magic-lilac mt-4" onClick={onContinue}>Continue</BringzzButton>
      )}
    </div>
  );
};

export default ShopperInvoiceStep;
